import React, { useState, useEffect, useRef } from "react";
import "./WorkflowManagement.css";
import { useNavigate } from 'react-router-dom';
import WorkflowHero from "./WorkflowHero";
import WorkflowWiseCode from "./WorkflowWiseCode";
import HRCopilotNewsSection from "./HRCopilotNewsSection";
import HRCopilotFeatures from "./HRCopilotFeatures";


const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="workflow-video-popup-overlay" onClick={onClose}>
      <div className="workflow-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="workflow-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="workflow-video-container">
          <video ref={videoRef} controls autoPlay className="workflow-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="workflow-video-popup-info">
          <h3>Workflow Management Platform</h3>
          <p>See how our workflow platform streamlines processes and boosts team productivity</p>
        </div>
      </div>
    </div>
  );
};

const WorkflowManagement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
    const videoRef = useRef(null);
    const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();
  const handleAccessRecruit = () => navigate('/access-recruit');

  // Add this array before your component or inside it
const processMetrics = [
  { id: 1, name: 'Document Processing', value: '1.2K', percentage: 85, trend: 'up', change: '+12%' },
  { id: 2, name: 'Approval Workflows', value: '856', percentage: 72, trend: 'up', change: '+8%' },
  { id: 3, name: 'Quality Checks', value: '432', percentage: 68, trend: 'down', change: '-3%' },
  { id: 4, name: 'Client Onboarding', value: '298', percentage: 91, trend: 'up', change: '+15%' },
  { id: 5, name: 'Invoice Processing', value: '674', percentage: 78, trend: 'up', change: '+6%' },
  { id: 6, name: 'Compliance Reviews', value: '543', percentage: 82, trend: 'up', change: '+9%' }
];

// Also add the workflowMetrics array if it's missing
const workflowMetrics = [
  { id: 1, name: 'Process Efficiency', value: 84, trend: 'up', change: '+12.5%' },
  { id: 2, name: 'Automation Rate', value: 76, trend: 'up', change: '+8.3%' },
  { id: 3, name: 'Error Rate', value: 2.4, trend: 'down', change: '-5.1%' },
  { id: 4, name: 'Cost Savings', value: 78, trend: 'up', change: '+23.7%' }
];

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-team-meeting-in-modern-office-4545-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

const workflowProcess = [
  {
    phase: "Design",
    icon: "🎨",
    time: "2-4 days",
    title: "Workflow Design & Mapping",
    steps: [
      "Process discovery and analysis",
      "Visual workflow mapping",
      "Stakeholder alignment sessions",
      "Approval workflow configuration"
    ]
  },
  {
    phase: "Build",
    icon: "⚙️",
    time: "3-7 days",
    title: "Automation Implementation",
    steps: [
      "Drag-and-drop workflow builder",
      "Integration configuration",
      "Rule-based automation setup",
      "Testing and validation"
    ]
  },
  {
    phase: "Deploy",
    icon: "🚀",
    time: "1-2 days",
    title: "Team Onboarding & Launch",
    steps: [
      "Team training sessions",
      "Permission configuration",
      "Production deployment",
      "Initial monitoring setup"
    ]
  },
  {
    phase: "Optimize",
    icon: "📈",
    time: "Ongoing",
    title: "Continuous Improvement",
    steps: [
      "Performance analytics review",
      "Bottleneck identification",
      "AI-driven optimization",
      "Process refinement cycles"
    ]
  }
];


const workflowIntegrations = [
  "Slack", "Microsoft Teams", "Jira", "Asana", 
  "Trello", "Salesforce", "HubSpot", "Zapier", 
  "Google Workspace", "Office 365", "ServiceNow", "SAP"
];


const handleAccessWorkflow = () => navigate('/payment');
  
const openWorkflowVideoPopup = () => {
  // Open workflow video popup
  console.log("Open workflow video");
};


  const features = [
    {
      icon: "📋",
      title: "Visual Workflow Builder",
      description: "Drag-and-drop interface to design, customize, and optimize complex workflows visually"
    },
    {
      icon: "🔄",
      title: "Process Automation",
      description: "Automate repetitive tasks and streamline approval processes with intelligent routing"
    },
    {
      icon: "👥",
      title: "Team Collaboration",
      description: "Real-time collaboration features with task assignments and progress tracking"
    },
    {
      icon: "📊",
      title: "Performance Analytics",
      description: "Comprehensive insights into workflow efficiency and bottleneck identification"
    },
    {
      icon: "🔔",
      title: "Smart Notifications",
      description: "Intelligent alerts and reminders to keep workflows moving and teams informed"
    },
    {
      icon: "🛡️",
      title: "Compliance & Security",
      description: "Built-in compliance checks, audit trails, and enterprise-grade security"
    }
  ];

  const workflowStages = [
    {
      phase: "Design & Planning",
      title: "Workflow Creation & Configuration",
      steps: ["Process Mapping", "Stakeholder Input", "Rule Definition", "Approval Setup"],
      time: "Planning Phase"
    },
    {
      phase: "Execution",
      title: "Active Workflow Management",
      steps: ["Task Assignment", "Progress Monitoring", "Issue Resolution", "Quality Checks"],
      time: "Execution Phase"
    },
    {
      phase: "Optimization",
      title: "Continuous Improvement",
      steps: ["Performance Analysis", "Bottleneck Identification", "Process Refinement", "Team Feedback"],
      time: "Ongoing"
    }
  ];

  return (
    <div className="workflow-container">
      {/* Scroll Progress Bar */}
      {/* <div className="workflow-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="workflow-background-animation">
        {/* <div className="workflow-floating-shape workflow-shape-1"></div>
        <div className="workflow-floating-shape workflow-shape-2"></div>
        <div className="workflow-floating-shape workflow-shape-3"></div>
        <div className="workflow-floating-shape workflow-shape-4"></div>
        <div className="workflow-floating-shape workflow-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="workflow-gradient-orb workflow-orb-1"></div>
        <div className="workflow-gradient-orb workflow-orb-2"></div>
        <div className="workflow-gradient-orb workflow-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="workflow-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="workflow-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(74, 144, 226, 0.08)" />
              <stop offset="50%" stopColor="rgba(120, 119, 216, 0.05)" />
              <stop offset="100%" stopColor="rgba(226, 74, 144, 0.08)" />
            </linearGradient>
            <linearGradient id="workflow-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(120, 119, 216, 0.06)" />
              <stop offset="50%" stopColor="rgba(119, 216, 216, 0.04)" />
              <stop offset="100%" stopColor="rgba(74, 226, 144, 0.06)" />
            </linearGradient>
            <pattern id="workflow-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#workflow-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#workflow-gradient1)"
            className="workflow-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#workflow-gradient2)"
            opacity="0.8"
            className="workflow-svg-path-2"
          />
        </svg>
      </div>

<WorkflowHero/>
<WorkflowWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>


    </div>
  );
};

export default WorkflowManagement;